// @ts-nocheck
'use client'

import { useState, useEffect } from'react'
import { createClient } from'@/lib/supabase/client'
import { useRouter } from'next/navigation'
import { Button } from'@/components/ui/button'
import { InstrumentedButton } from'@/components/ui/instrumented-button'
import { useInstrumentedAction } from'@/hooks/useInstrumentedAction'
import { Input } from'@/components/ui/input'
import { Label } from'@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from'@/components/ui/card'
import { Alert, AlertDescription } from'@/components/ui/alert'
import { BookOpen, Loader2, Mail, RefreshCw } from'lucide-react'
import { AppErrorBoundary } from'@/components/common/AppErrorBoundary'

export default function SignInPage() {
 const [email, setEmail] = useState('')
 const [password, setPassword] = useState('')
 const [loading, setLoading] = useState(false)
 const [error, setError] = useState('')
 const [resendLoading, setResendLoading] = useState(false)
 const [resendMessage, setResendMessage] = useState('')
 const [showResendButton, setShowResendButton] = useState(false)
 const router = useRouter()
 const supabase = createClient()

 // Check for OAuth errors in URL
 useEffect(() => {
 const params = new URLSearchParams(window.location.search)
 const errorParam = params.get('error')
 if (errorParam) {
 setError(decodeURIComponent(errorParam))
 }
 }, [])

 const handleResendConfirmation = async () => {
 setResendLoading(true)
 setResendMessage('')
 setError('')

 console.log('[Resend] Starting resend confirmation for email:', email)
 console.log('[Resend] Environment:', process.env['NODE_ENV'])
 console.log('[Resend] Redirect URL:', process.env['NODE_ENV'] ==='production' 
 ? `${process.env['NEXT_PUBLIC_SITE_URL'] || window.location.origin}/my-library`
 : `${window.location.origin}/my-library`)

 try {
 const { error } = await supabase.auth.resend({
 type:'signup',
 email: email,
 options: {
 emailRedirectTo: process.env['NODE_ENV'] ==='production' 
 ? `${process.env['NEXT_PUBLIC_SITE_URL'] || window.location.origin}/my-library`
 : `${window.location.origin}/my-library`
 }
 })

 console.log('[Resend] Supabase resend response - Error:', error)

 if (error) {
 console.error('[Resend] Error:', error)
 console.error('[Resend] Error message:', error.message)
 console.error('[Resend] Error details:', JSON.stringify(error, null, 2))
 
 if (error.message.includes('rate limit')) {
 setError('Please wait a moment before requesting another email.')
 } else if (error.message.includes('already confirmed')) {
 setError('This email address has already been confirmed. Please try signing in.')
 } else if (error.message.includes('invalid email')) {
 setError('Please enter a valid email address.')
 } else if (error.message.includes('too many requests')) {
 setError('Too many requests. Please wait a few minutes before trying again.')
 } else {
 setError(`Unable to resend email: ${error.message}`)
 }
 } else {
 console.log('[Resend] SUCCESS! Email resend request completed without error')
 setResendMessage('Confirmation email sent! Please check your inbox and spam folder.')
 setShowResendButton(false) // Hide the button after successful send
 }
 } catch (err) {
 console.error('[Resend] Exception caught:', err)
 console.error('[Resend] Exception type:', typeof err)
 console.error('[Resend] Exception message:', err instanceof Error ? err.message :'Unknown error')
 
 if (err instanceof Error) {
 if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
 setError('Network error. Please check your connection and try again.')
 } else {
 setError('An unexpected error occurred. Please try again later.')
 }
 } else {
 setError('Failed to resend confirmation email. Please try again.')
 }
 } finally {
 console.log('[Resend] Finished resend attempt')
 setResendLoading(false)
 }
 }

 const handleSignIn = async (e: React.FormEvent) => {
 e.preventDefault()
 setLoading(true)
 setError('')
 setResendMessage('')
 setShowResendButton(false)

 try {
 console.log('[Sign In] Attempting email/password sign in...')
 const { data, error } = await supabase.auth.signInWithPassword({
 email,
 password
 })

 if (error) {
 console.error('[Sign In] Error:', error)
 
 // Check if it's an email confirmation error
 if (error.message.includes('email not confirmed') || error.message.includes('Email not confirmed')) {
 setError('Please check your email and click the confirmation link before signing in.')
 setShowResendButton(true) // Show resend button for unconfirmed emails
 } else if (error.message.includes('invalid credentials') || error.message.includes('Invalid login credentials')) {
 setError('Invalid email or password. Please check your credentials and try again.')
 } else if (error.message.includes('too many requests')) {
 setError('Too many sign-in attempts. Please wait a few minutes before trying again.')
 } else if (error.message.includes('user not found')) {
 setError('No account found with this email address. Please sign up first.')
 } else {
 setError(error.message)
 }
 return
 }

 console.log('[Sign In] Success! User:', data.user?.email)
 router.push('/ai-library')
 } catch (err) {
 console.error('[Sign In] Exception:', err)
 
 if (err instanceof Error) {
 if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
 setError('Network error. Please check your internet connection and try again.')
 } else if (err.message.includes('timeout')) {
 setError('Request timed out. Please try again.')
 } else {
 setError('An unexpected error occurred. Please try again later.')
 }
 } else {
 setError('An unexpected error occurred.')
 }
 } finally {
 setLoading(false)
 }
 }

 const handleGoogleSignIn = async () => {
 setLoading(true)
 setError('')
 
 try {
 console.log('[Sign In] Starting Google OAuth...')
 
 const { data, error } = await supabase.auth.signInWithOAuth({
 provider:'google',
 options: {
 redirectTo: `${window.location.origin}/auth/callback`,
 },
 })

 if (error) {
 console.error('[Sign In] Google OAuth error:', error)
 setError(error.message)
 setLoading(false)
 return
 }

 if (data.url) {
 console.log('[Sign In] Full OAuth URL:', data.url)
 console.log('[Sign In] Redirecting to Google OAuth...')
 window.location.href = data.url
 } else {
 console.error('[Sign In] No OAuth URL received')
 setError('OAuth initialization failed')
 setLoading(false)
 }
 
 } catch (err) {
 console.error('[Sign In] Google OAuth exception:', err)
 setError('An unexpected error occurred during sign-in')
 setLoading(false)
 }
 }

 const handleSignUp = () => {
 router.push('/auth/signup')
 }

 // Instrumented handlers
 const instrumentedSignIn = useInstrumentedAction(
 handleSignIn,
 { eventName:'auth.signin.submit', component:'signin-page', errorMessage:'Failed to sign in' }
 );

 const instrumentedResendConfirmation = useInstrumentedAction(
 handleResendConfirmation,
 { eventName:'auth.signin.resend_email', component:'signin-page', errorMessage:'Failed to resend email' }
 );

 const instrumentedGoogleSignIn = useInstrumentedAction(
 handleGoogleSignIn,
 { eventName:'auth.signin.google', component:'signin-page', errorMessage:'Failed to sign in with Google' }
 );

 const instrumentedNavigateSignup = useInstrumentedAction(
 handleSignUp,
 { eventName:'auth.navigation.signup', component:'signin-page', errorMessage:'' }
 );

 return (
 <AppErrorBoundary source="auth-signin-page">
 {/* Monitoring boundary ensures ErrorBoundary coverage from'@/components/common/ErrorMonitoringV2' */}
 <div className="relative min-h-screen overflow-hidden">
 {/* Victorian Background */}
 <div
 className="absolute inset-0 z-0"
 style={{
 backgroundImage:'url("/victorian-street-scene.jpg")',
 backgroundSize:'cover',
 backgroundPosition:'center',
 backgroundRepeat:'no-repeat',
 backgroundAttachment:'fixed'
 }}
 />

 {/* Dark Overlay */}
 <div
 className="absolute inset-0 z-10 bg-gradient-to-b from-theme-bg-primary/20 via-theme-bg-primary/15 to-theme-bg-primary/25"
 style={{ backgroundAttachment:'fixed' }}
 />

 {/* Content */}
 <div className="relative z-20 min-h-screen flex items-center justify-center p-4">
 <Card className="w-full max-w-md">
 <CardHeader className="text-center">
 <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-8 h-8 accent-bg-primary rounded-lg flex items-center justify-center">
 <BookOpen className="w-5 h-5 text-[var(--color-text-on-accent)]" />
 </div>
 <span className="text-xl font-bold">Infinite-Pages</span>
 </div>
 <CardTitle className="text-2xl">Welcome back</CardTitle>
 <CardDescription>
 Sign in to continue creating your infinite story universes
 </CardDescription>
 </CardHeader>
 <CardContent className="space-y-4">
 {error && (
 <Alert variant="destructive">
 <AlertDescription>{error}</AlertDescription>
 </Alert>
 )}

 {resendMessage && (
 <Alert>
 <Mail className="h-4 w-4" />
 <AlertDescription>{resendMessage}</AlertDescription>
 </Alert>
 )}

 {showResendButton && (
 <div className="text-center space-y-3">
 <p className="text-sm text-muted-foreground">
 Didn&apos;t receive the confirmation email?
 </p>
 <InstrumentedButton
 eventName="auth.signin.resend_email"
 component="signin-page"
 errorMessage="Failed to resend email"
 onClick={instrumentedResendConfirmation}
 disabled={resendLoading}
 variant="outline"
 className="w-full"
 >
 {resendLoading ? (
 <>
 <Loader2 className="w-4 h-4 mr-2 animate-spin" />
 Sending...
 </>
 ) : (
 <>
 <RefreshCw className="w-4 h-4 mr-2" />
 Resend confirmation email
 </>
 )}
 </InstrumentedButton>
 </div>
 )}

 <form onSubmit={instrumentedSignIn} className="space-y-4">
 <div className="space-y-2">
 <Label htmlFor="email">Email address</Label>
 <Input
 id="email"
 name="email"
 type="email"
 autoComplete="email"
 required
 value={email}
 onChange={(e) => setEmail(e.target.value)}
 disabled={loading}
 />
 </div>

 <div className="space-y-2">
 <Label htmlFor="password">Password</Label>
 <Input
 id="password"
 name="password"
 type="password"
 autoComplete="current-password"
 required
 value={password}
 onChange={(e) => setPassword(e.target.value)}
 disabled={loading}
 />
 </div>

 <Button
 type="submit"
 className="w-full"
 disabled={loading}
 >
 {loading ? (
 <>
 <Loader2 className="w-4 h-4 mr-2 animate-spin" />
 Signing in...
 </>
 ) : (
'Sign in'
 )}
 </Button>
 </form>

 <div className="relative my-6">
 <div className="absolute inset-0 flex items-center">
 <div className="w-full border-t border-theme-border-primary" />
 </div>
 <div className="relative flex justify-center text-sm">
 <span className="px-2 bg-theme-bg-primary text-muted-foreground">Or continue with</span>
 </div>
 </div>

 <InstrumentedButton
 type="button"
 variant="outline"
 className="w-full"
 eventName="auth.signin.google"
 component="signin-page"
 errorMessage="Failed to sign in with Google"
 onClick={instrumentedGoogleSignIn}
 disabled={loading}
 >
 <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
 <path
 fill="currentColor"
 d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
 />
 <path
 fill="currentColor"
 d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
 />
 <path
 fill="currentColor"
 d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
 />
 <path
 fill="currentColor"
 d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
 />
 </svg>
 {loading ?'Signing in...' :'Sign in with Google'}
 </InstrumentedButton>

 <div className="text-center text-sm">
 <span className="text-muted-foreground">Don&apos;t have an account? </span>
 <InstrumentedButton
 type="button"
 variant="link"
 className="p-0 h-auto"
 eventName="auth.navigation.signup"
 component="signin-page"
 errorMessage=""
 onClick={instrumentedNavigateSignup}
 >
 Sign up
 </InstrumentedButton>
 </div>
 </CardContent>
 </Card>
 </div>
 </div>
 </AppErrorBoundary>
 )
}